import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import type { Priority } from '@sharedlib/common/types/tasking-app/priority';
import { type Observable } from 'rxjs';
import { type Task } from '../types/task';

export interface TaskVersion {
  readonly version: number;
  readonly kind: 'created' | 'baseline' | 'edited' | 'restored';
  readonly author: { readonly id: string; readonly name: string | null } | null;
  readonly at: string;
  /** The fields this save changed, by their stored names (title, description, completed, priority, …). */
  readonly fields: readonly string[];
  readonly restoredFrom: number | null;
  readonly review: 'requested' | 'approved' | 'changes_requested' | null;
}
export interface TaskVersionsPage {
  readonly items: readonly TaskVersion[];
  readonly current: number | null;
  readonly total: number;
}
/** One saved version with the task's content as it was saved then. */
export interface SavedTaskVersion extends TaskVersion {
  readonly title: string | null;
  readonly description: string | null;
  readonly completed: boolean | null;
  readonly priority: Priority | null;
  readonly startDateTime: string | null;
  readonly endDateTime: string | null;
  readonly allDay: boolean | null;
  readonly recurrence: string | null;
}
/** `task` as PUT returns it; `restored: false` changed nothing. */
export interface TaskRestoreAnswer {
  readonly task: Partial<Task>;
  readonly version: number;
  readonly restored: boolean;
}

/**
 * tasking-app's version routes (#2071), always under the task's own workspace. Provided per component, never at root: only
 * the space-context subtree's HttpClient carries the spaces interceptor that turns the owning-tenant context into the
 * request's space_id; the root client would send none (#2074).
 */
@Injectable()
export class TaskVersionsClient {
  private readonly http = inject(HttpClient);

  public list(task: { id: string; accountId: string }, offset: number): Observable<TaskVersionsPage> {
    return this.http.get<TaskVersionsPage>(this.url(task), {
      params: new HttpParams().set('offset', offset).set('limit', 25),
      context: owningTenantContext(task.accountId),
    });
  }
  public read(task: { id: string; accountId: string }, version: number): Observable<SavedTaskVersion> {
    return this.http.get<SavedTaskVersion>(`${this.url(task)}/${version}`, {
      context: owningTenantContext(task.accountId),
    });
  }
  public restore(task: { id: string; accountId: string }, version: number): Observable<TaskRestoreAnswer> {
    return this.http.post<TaskRestoreAnswer>(`${this.url(task)}/${version}/restore`, null, {
      context: owningTenantContext(task.accountId),
    });
  }
  private url(task: { id: string }): string {
    return `${environment.baseUrl}/api/tasking/tasks/${encodeURIComponent(task.id)}/versions`;
  }
}
